<template>
    <div class="modal">
        <div
            ref="dialog"
            class="modal-content"
            role="dialog"
            aria-modal="true"
            tabindex="-1"
            :aria-labelledby="titleId"
            :aria-describedby="bodyId"
            data-test-id="confirm-modal"
            @keydown.esc.prevent="dismiss"
            @keydown.tab="trapTab"
        >
            <header class="modal__header">
                <h2
                    :id="titleId"
                    class="modal__title"
                    data-test-id="confirm-modal-title"
                >
                    <slot name="title">{{ label }}</slot>
                </h2>
            </header>

            <div
                :id="bodyId"
                class="modal__body"
                data-test-id="confirm-modal-body"
            >
                <slot name="body">{{ text }}</slot>
            </div>

            <footer class="modal__footer">
                <button
                    ref="cancelButton"
                    class="modal__btn modal__btn--cancel"
                    data-test-id="confirm-modal-cancel"
                    @click="cancel"
                >
                    Cancel
                </button>

                <button
                    class="modal__btn modal__btn--confirm"
                    data-test-id="confirm-modal-confirm"
                    @click="confirm"
                >
                    Confirm
                </button>
            </footer>
        </div>
    </div>
</template>

<script>
import { getCurrentInstance } from 'vue'

const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',')

export default {
    props: {
        text: String,
        label: String,
        confirm: Function,
        cancel: Function,
    },

    computed: {
        // Leading letter on purpose: uid is a number, and an id starting with
        // a digit is legal HTML but an invalid CSS selector.
        titleId() {
            return `confirm-modal-${this.uid}-title`
        },
        bodyId() {
            return `confirm-modal-${this.uid}-body`
        },
        uid() {
            return getCurrentInstance().uid
        },
    },

    // The modal container is rendered ahead of <router-view> in App.vue, so
    // everything in the dialog sits BEFORE the page in document order. Focus
    // stayed on whatever opened the modal, and Tab walked forward through the
    // rest of the page and out of the document without ever reaching the
    // dialog. Moving focus in on open and trapping it makes document order
    // irrelevant (WCAG 2.1 SC 2.4.3).
    mounted() {
        this.previouslyFocused = document.activeElement

        // Cancel, not Confirm: these prompts guard destructive actions, so a
        // stray Enter on open must not commit one.
        this.$refs.cancelButton?.focus()
    },

    // Restoring focus to a control that a confirmed action has destroyed or
    // disabled is a silent no-op that drops focus to <body>, and Tab then
    // restarts at the left-hand nav. Only restore when the opener can actually
    // take focus; when it cannot, the page that owns the action is responsible
    // for placing focus once its write settles.
    unmounted() {
        if (this.canRestoreFocusTo(this.previouslyFocused)) {
            this.previouslyFocused.focus()
        }
    },

    methods: {
        canRestoreFocusTo(element) {
            return Boolean(element) && typeof element.focus === 'function' && element.isConnected && !element.disabled
        },

        dismiss() {
            if (typeof this.cancel === 'function') {
                this.cancel()
            }
        },

        // Bound on the dialog, so it only ever sees Tab from inside it: an
        // event raised outside never bubbles through this element.
        trapTab(event) {
            const focusable = Array.from(this.$refs.dialog.querySelectorAll(FOCUSABLE))

            if (focusable.length === 0) {
                return
            }

            const first = focusable[0]
            const last = focusable[focusable.length - 1]
            const active = document.activeElement

            if (event.shiftKey && active === first) {
                event.preventDefault()
                last.focus()

                return
            }

            if (!event.shiftKey && active === last) {
                event.preventDefault()
                first.focus()
            }
        },
    },
}
</script>

<style scoped>
.modal {
    position: absolute;
    z-index: 5000;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(33, 38, 40, 0.4);
}

.modal-content {
    width: 412px;
    background-color: #212628;
    color: #ffffff;
    font-size: 18px;
    font-family: 'Avenir Book';
    padding: 20px;
    border-radius: 5px;
}

.modal__header {
    border-bottom: 0.5px solid #636a6d;
    width: 100%;
    padding-bottom: 10px;
    font-size: 1.25rem;
}

.modal__body {
    font-size: 14px;
    padding: 10px 0 10px 0;
}

.modal__footer {
    display: flex;
    justify-content: space-between;
}

.modal__btn {
    padding: 7px 20px;
    border-radius: 8px;
    border: none;
    color: #ffffff;
    cursor: pointer;
    font-size: 1rem;
}

.modal__btn--cancel {
    background-color: #434a4d;
}

.modal__btn--confirm {
    background-color: #2196f3;
}
</style>
